import React from 'react';

import {SystemColumnName} from 'data-frames/DataFrameConstants';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {convertGQLToRESTOutlierConfig} from 'insights/analytics-visualizations/data/AnalyticsVisualizationGQLRESTConverters';
import {
  NumericRegressionDataset,
  RegressionAnalysis,
} from 'ui-library/charting/statistics/regression/RegressionTypes';
import {AnalysisStatus} from 'ui-library/charting/statistics/Types';
import {AsyncState, AsyncStatus, makeAsyncCall} from 'ui-library/hooks/useAsyncState';
import LatexEquation, {EquationDisplayMode} from 'ui-library/LatexEquation';

import {R_SQUARED} from './Constants';
import {LogisticCurveFitData, runLogisticRegression} from './RegressionDAO';
import {RegressionConfig} from './RegressionTypes';

type OutlierConfig = NonNullable<
  ExtractByTypename<
    AnalyticsVisualization['parameters'],
    'AnalyticsVisualizationScatterPlotParametersNode'
  >['outlierConfig']
>;

/**
 * Interface is adapted from return type for insights/statistics/hill_curve_regression/HillCurveFit
 * and should be kept in sync
 */
export interface LogisticRegressionResults {
  detectedOutliers: Array<string>;
  min: number;
  max: number;
  logIC50: number;
  hillSlope: number;
  ic50: number;
  R2: number;
  RMSE: number;
  relativeLikelihoodOfNull: number;
}

export type LogisticRegressionAnalysis = RegressionAnalysis<LogisticRegressionResults>;

/**
 * Equation is adapted from insights/statistics/hill_curve_regression _hill_equation
 * and should be kept in sync.
 */
export function estimatePointY(
  pointX: number,
  modelResults: Pick<LogisticRegressionResults, 'min' | 'max' | 'logIC50' | 'hillSlope'>
): number {
  return (
    modelResults.min +
    (modelResults.max - modelResults.min) /
      (1.0 + 10.0 ** ((modelResults.logIC50 - Math.log10(pointX)) * modelResults.hillSlope))
  );
}

export function convertDatasetToCurveFitData(
  dataset: NumericRegressionDataset,
  regressionConfig: RegressionConfig,
  outlierConfig: OutlierConfig | null = null
): LogisticCurveFitData {
  return {
    fitFunctionToColumnMap: {rowIds: SystemColumnName.ROW_ID, xValues: 'x', yValues: 'y'},
    dataframe: {
      dataTypes: {
        [SystemColumnName.ROW_ID]: dataset.axisTypes.benchcol_row_id,
        x: dataset.axisTypes.x,
        y: dataset.axisTypes.y,
      },
      points: dataset.points.map((point) => ({
        [SystemColumnName.ROW_ID]: point.benchcol_row_id,
        x: point.x,
        y: point.y,
      })),
    },
    outlierConfig: outlierConfig != null ? convertGQLToRESTOutlierConfig(outlierConfig) : null,
    regressionConfig,
  };
}

export const LOGISTIC_REGRESSION_TOOLTIP_DISCLAIMER = (
  <p>
    For non-linear models, <LatexEquation equation="R^2" displayMode={EquationDisplayMode.INLINE} /> does not
    necessarily capture goodness-of-fit. We also include a p-value for how likely it is that the observations
    occurred by random chance, rather than following a logistic distribution. Lower p-values imply a more
    statistically significant model fit.
  </p>
);

export function convertAsyncRegressionStateToAnalysis(
  asyncState: AsyncState<LogisticRegressionResults, Array<LogisticCurveFitData>>
): LogisticRegressionAnalysis {
  if (asyncState.status === AsyncStatus.NOT_FETCHED || asyncState.status === AsyncStatus.LOADING) {
    return {
      status: AnalysisStatus.LOADING,
    };
  } else if (asyncState.status === AsyncStatus.ERROR) {
    return {
      status: AnalysisStatus.ERROR,
      message: asyncState.message,
    };
  } else {
    const fitOutput = asyncState.data;
    return {
      status: AnalysisStatus.SUCCESS,
      result: {
        getEstimatedY: (x) => estimatePointY(x as number, fitOutput),
        detectedOutliers: new Set(fitOutput.detectedOutliers),
        fitEquation: null,
        outputValues: [
          {fieldName: 'IC50', showInPlotHeader: true, value: fitOutput.ic50},
          {fieldName: 'Slope', showInPlotHeader: false, value: fitOutput.hillSlope},
          {fieldName: 'Min', showInPlotHeader: false, value: fitOutput.min},
          {fieldName: 'Max', showInPlotHeader: false, value: fitOutput.max},
          {fieldName: 'p', showInPlotHeader: true, value: fitOutput.relativeLikelihoodOfNull},
          {fieldName: 'R2', fieldNameDisplayOverride: R_SQUARED, showInPlotHeader: true, value: fitOutput.R2},
        ],
        fitOutput,
        tooltipDisclaimer: LOGISTIC_REGRESSION_TOOLTIP_DISCLAIMER,
      },
    };
  }
}

export function performLogisticRegressionAnalysis(
  dataset: NumericRegressionDataset,
  regressionConfig: RegressionConfig,
  setRegressionState: (newRegressionState: LogisticRegressionAnalysis) => void,
  outlierConfig: OutlierConfig | null
): void {
  makeAsyncCall({
    makeCall: runLogisticRegression,
    args: [convertDatasetToCurveFitData(dataset, regressionConfig, outlierConfig)],
    asyncState: {status: AsyncStatus.NOT_FETCHED},
    setAsyncState: (newAsyncState) => {
      setRegressionState(convertAsyncRegressionStateToAnalysis(newAsyncState));
    },
  });
}
